import React from 'react';
import { ArrowRight, Zap, TrendingUp } from 'lucide-react';

const Hero = () => {
  const scrollToContact = (e) => {
    e.preventDefault();
    const element = document.querySelector('#contatti');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToServices = (e) => {
    e.preventDefault();
    const element = document.querySelector('#servizi');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      data-testid="hero-section" 
      className="relative min-h-screen flex items-center pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden"
    >
      {/* Background Effects */}
      <div className="hero-glow" />
      <div className="hero-glow-secondary" />
      
      {/* Abstract background pattern */}
      <div className="absolute inset-0 opacity-5">
        <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
              <path d="M 10 0 L 0 0 0 10" fill="none" stroke="currentColor" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>

      <div className="container-custom relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <div className="animate-fade-in-up">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-8">
              <Zap className="w-4 h-4 text-[#F5A547]" />
              <span className="text-sm text-white/70 font-medium">Leader nella Consulenza Energetica</span>
            </div>

            {/* Main Heading */}
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-[1.1] mb-6">
              Riduci i Costi{' '}
              <span className="gradient-text">Energetici</span>
              <br />
              della Tua Azienda
            </h1>

            {/* Subheading */}
            <p className="text-base md:text-lg text-white/60 leading-relaxed mb-10 max-w-xl">
              Consulenza strategica per microbusiness, PMI ed aziende energivore.
              <br />
              Ottimizza i consumi ed accedi a finanziamenti agevolati e trasforma l'energia in un vantaggio competitivo.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={scrollToContact}
                className="btn-primary bg-[#F5A547] text-black font-bold px-8 py-4 rounded-full flex items-center justify-center gap-2 neon-orange"
                data-testid="hero-cta-primary"
              >
                Richiedi Consulenza Gratuita
                <ArrowRight className="w-5 h-5" />
              </button>
              <button
                onClick={scrollToServices}
                className="btn-secondary border border-[#5CBDB0] text-[#5CBDB0] px-8 py-4 rounded-full font-semibold flex items-center justify-center gap-2"
                data-testid="hero-cta-secondary"
              >
                Scopri i Servizi
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 mt-14 pt-8 border-t border-white/10">
              <div className="stat-card p-4 rounded-xl text-center">
                <div className="font-heading text-2xl md:text-3xl font-bold text-[#5CBDB0]">500+</div>
                <div className="text-xs md:text-sm text-white/50 mt-1">Clienti Soddisfatti</div>
              </div>
              <div className="stat-card p-4 rounded-xl text-center">
                <div className="font-heading text-2xl md:text-3xl font-bold text-[#F5A547]">30%</div>
                <div className="text-xs md:text-sm text-white/50 mt-1">Risparmio Medio</div>
              </div>
              <div className="stat-card p-4 rounded-xl text-center">
                <div className="font-heading text-2xl md:text-3xl font-bold text-[#C14B7E]">5M+</div>
                <div className="text-xs md:text-sm text-white/50 mt-1">Finanziamenti Ottenuti</div>
              </div>
            </div>
          </div>

          {/* Right Content - Visual */}
          <div className="hidden lg:block relative animate-fade-in-up stagger-2">
            <div className="relative">
              {/* Main Card */}
              <div className="glass-card rounded-3xl p-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#5CBDB0]/10 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-[#F5A547]/10 rounded-full blur-3xl" />
                
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-[#5CBDB0]/20 flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-[#5CBDB0]" />
                    </div>
                    <div>
                      <div className="text-white font-semibold">Analisi Energetica</div>
                      <div className="text-white/50 text-sm">Monitoraggio in tempo reale</div>
                    </div>
                  </div>

                  {/* Chart Visualization */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="flex-1 h-3 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full w-[75%] bg-gradient-to-r from-[#5CBDB0] to-[#5CBDB0]/50 rounded-full" />
                      </div>
                      <span className="text-sm text-white/70 w-16">-25%</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex-1 h-3 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full w-[60%] bg-gradient-to-r from-[#F5A547] to-[#F5A547]/50 rounded-full" />
                      </div>
                      <span className="text-sm text-white/70 w-16">-40%</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex-1 h-3 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full w-[85%] bg-gradient-to-r from-[#C14B7E] to-[#C14B7E]/50 rounded-full" />
                      </div>
                      <span className="text-sm text-white/70 w-16">-15%</span>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
